import React from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  Box,
  Grid,
  Typography,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
} from "@mui/material";
import moment from "moment";

const parseAndFormatDate = (dateString) => {
  const date = new Date(dateString);
  return moment(date).locale("en").format("MMMM YYYY");
};

// helpers format & safe number (tanpa ??)
const toNum = (v) => Number(v || 0);
const fmtUSD = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    toNum(v)
  );
const fmtIDR = (v) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR" }).format(
    toNum(v)
  );

// Small stat card
const StatCard = ({ label, value, accent = "default" }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.5,
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="subtitle1"
      sx={{
        fontWeight: 700,
        mt: 0.25,
        color: accent === "danger" ? "error.main" : undefined,
      }}
    >
      {value}
    </Typography>
  </Paper>
);

const InvoicePDF = ({ data }) => {
  const getMonthText = (numericMonth) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[numericMonth - 1] || "";
  };

  const generatePDF = () => {
    const pdf = new jsPDF({ orientation: "landscape", unit: "pt" });
    const margin = 36;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const contentWidth = pageWidth - margin * 2;

    // ---- WIB (Asia/Jakarta) time for invoice_created ----
    const rawCreated = new Date(data.invoice_created);

    // tambah offset +7 jam (WIB)
    const createdJakarta = new Date(rawCreated.getTime() + 7 * 60 * 60 * 1000);

    const created_invoice = moment(createdJakarta)
      .locale("id")
      .format("DD MMMM YYYY HH:mm:ss");

    // ---- ambil rate nominal (fallback dari item pertama) ----
    var rateRp =
      data && data.nominal_rate
        ? data.nominal_rate
        : data && data.invoice_items && data.invoice_items.length
        ? data.invoice_items[0].nominal_rate
        : 0;

    // ===== HEADER =====
    pdf.setFontSize(12).setFont(undefined, "bold");
    pdf.text(`Invoice: ${data.invoice_number}`, margin, margin);
    pdf.text(`Created: ${created_invoice} WIB`, pageWidth - margin, margin, {
      align: "right",
    });

    // ===== INFO DETAIL =====
    const infoLines = [
      data.custom === 1
        ? `Period: ${moment(data.from_date).locale("en").format("MMMM YYYY")}`
        : `Period: ${getMonthText(data.periode)} ${data.year}`,
      `Date: ${moment(data.from_date).format("DD MMM YYYY")} - ${moment(
        data.to_date
      ).format("DD MMM YYYY")}`,
      `Agent: ${data.agent_name}`,
    ];
    pdf.setFontSize(10).setFont(undefined, "normal");
    let infoY = margin + 16;
    infoLines.forEach((line) => {
      pdf.text(String(line), margin, infoY);
      infoY += 14;
    });

    // ===== SUMMARY (TOP) =====
    const summaryRows = [
      ["Total Profit ($)", fmtUSD(data.sub_total_all_profit)],
      ["Total Profit (Rp)", fmtIDR(data.sub_total_all_profit_rp)],
      ["Rate (Rp / USD)", `${fmtIDR(rateRp)} / USD`],
      ["Total MC ($)", fmtUSD(data.total_mc)],
      ["Total SL ($)", fmtUSD(data.total_sl)],
      ["Total Cut Loss ($)", fmtUSD(data.total_cutloss)],
      // ["Total Loss Profit ($)", fmtUSD(data.loss_profit)],
    ];
    if (Number(data.custom) === 0) {
      summaryRows.push([
        `Fee (${toNum(data.shared_profit)}%)`,
        fmtIDR(data.fee_final_rp),
      ]);
    }

    pdf.setFontSize(13).setFont(undefined, "bold");
    pdf.text("Summary", margin, infoY + 8);

    pdf.autoTable({
      startY: infoY + 16,
      head: [],
      body: summaryRows.map(([l, v]) => [String(l), String(v)]),
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 6 },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.55 },
        1: {
          cellWidth: contentWidth * 0.45,
          halign: "right",
          fontStyle: "bold",
        },
      },
      margin: { left: margin, right: margin },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    // ===== TABEL ITEM =====
    const afterSummaryY = pdf.lastAutoTable.finalY + 20;
    pdf.setFontSize(13).setFont(undefined, "bold");
    pdf.text("Invoice Items", margin, afterSummaryY);

    const itemHeaders = [
      "No",
      "Account",
      "MC ($)",
      "SL ($)",
      "Cut Loss ($)",
      // "Loss Profit ($)",
      "Profit ($)",
      "Profit (Rp)",
    ];

    // Account -> dua baris: accountNumber \n alias (STRING)
    const itemRows = data.invoice_items.map((item, i) => {
      const account = String(item.account_number || "-");
      const alias = String(item.alias || "-");
      const accountCell = `${account}\n${alias}`;
      return [
        String(i + 1),
        accountCell,
        String(fmtUSD(item.total_mc)),
        String(fmtUSD(item.total_sl)),
        String(fmtUSD(item.total_cutloss)),
        // String(fmtUSD(item.loss_profit)),
        String(fmtUSD(item.final_profit_usd)),
        String(fmtIDR(item.final_profit_usd_x_rate_rp)),
      ];
    });

    const noW = 30;
    const avail = contentWidth - noW;

    pdf.autoTable({
      head: [itemHeaders],
      body: itemRows,
      startY: afterSummaryY + 8,
      tableWidth: contentWidth,
      theme: "grid",
      styles: {
        fontSize: 7,
        cellPadding: { top: 4, right: 4, bottom: 4, left: 4 },
        overflow: "linebreak",
        lineWidth: 0.4,
      },
      headStyles: {
        halign: "center",
        fillColor: [245, 245, 245],
        textColor: [25, 118, 210],
        fontStyle: "bold",
        fontSize: 8,
      },
      bodyStyles: { valign: "middle" },
      columnStyles: {
        0: { cellWidth: noW, halign: "center" },
        1: { cellWidth: avail * 0.22, halign: "left", lineHeight: 1.1 },
        2: { cellWidth: avail * 0.12, halign: "right" },
        3: { cellWidth: avail * 0.12, halign: "right" },
        4: { cellWidth: avail * 0.14, halign: "right" },
        // 5: { cellWidth: avail * 0.1, halign: "right" },
        5: { cellWidth: avail * 0.2, halign: "right" },
        6: { cellWidth: avail * 0.2, halign: "right" },
      },
      margin: { left: margin, right: margin },
      didParseCell: (cell) => {
        if (cell.section === "body" && cell.column.index >= 2) {
          cell.cell.styles.fontStyle = "bold";
        }
      },
    });

    const filename = `${data.invoice_number}_${data.agent_name}`.replace(
      /\s+/g,
      "_"
    );
    pdf.save(`${filename}.pdf`);
  };

  const rawCreated = new Date(data.invoice_created);
  const createdWIB =
    moment(new Date(rawCreated.getTime() + 7 * 60 * 60 * 1000))
      .locale("id")
      .format("DD MMM YYYY HH:mm:ss") + " WIB";

  const rateRp =
    (data && data.nominal_rate) ||
    (data && data.invoice_items && data.invoice_items.length
      ? data.invoice_items[0].nominal_rate
      : 0);

  const InfoField = ({ label, value }) => (
    <Box sx={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
            Invoice Preview
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Created: {createdWIB}
          </Typography>
        </Box>
        <Button variant="contained" onClick={generatePDF}>
          Generate PDF
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InfoField label="Invoice Number" value={data.invoice_number} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField
                  label="Agent"
                  value={String(data.agent_name || "-")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField
                  label="Period"
                  value={
                    data.custom === 0
                      ? parseAndFormatDate(data.to_date)
                      : parseAndFormatDate(data.from_date)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField
                  label="Date Range"
                  value={`${moment(data.from_date)
                    .locale("en")
                    .format("DD MMM YYYY")} - ${moment(data.to_date)
                    .locale("en")
                    .format("DD MMM YYYY")}`}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Chip
              label={`Rate: ${fmtIDR(rateRp).replace("Rp", "Rp ")}`}
              variant="outlined"
            />
            {Number(data.custom) === 0 && (
              <Chip
                color="primary"
                label={`Fee: ${toNum(data.shared_profit)}%`}
                variant="outlined"
              />
            )}
          </Paper>
        </Grid>
      </Grid>
      <Grid container spacing={1.25} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={3} lg={2.4}>
          <StatCard
            label="Total Profit($)"
            value={fmtUSD(data.sub_total_all_profit)}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2.4}>
          <StatCard
            label="Total Profit (Rp)"
            value={fmtIDR(data.sub_total_all_profit_rp)}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2.4}>
          <StatCard label="Total MC ($)" value={fmtUSD(data.total_mc)} />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2.4}>
          <StatCard label="Total SL ($)" value={fmtUSD(data.total_sl)} />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2.4}>
          <StatCard
            label="Total Cut Loss ($)"
            value={fmtUSD(data.total_cutloss)}
          />
        </Grid>
        {/* <Grid item xs={6} sm={4} md={3} lg={2.4}>
          <StatCard
            label="Total Loss Profit ($)"
            value={fmtUSD(data.loss_profit)}
            accent="danger"
          />
        </Grid> */}

        {Number(data.custom) === 0 && (
          <Grid item xs={6} sm={4} md={3} lg={2.4}>
            <StatCard
              label={`Fee (${toNum(data.shared_profit)}%)`}
              value={fmtIDR(data.fee_final_rp)}
            />
          </Grid>
        )}
      </Grid>
      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Invoice Items
          </Typography>
        </Box>

        <TableContainer sx={{ maxHeight: 460 }}>
          <Table stickyHeader size="small" aria-label="invoice-items">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }}>No</TableCell>
                <TableCell>Account</TableCell>
                <TableCell align="center">Comments</TableCell>
                {/* <TableCell align="right">Loss ($)</TableCell> */}
                <TableCell align="right">Profit ($)</TableCell>
                <TableCell align="right">Profit (Rp)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.invoice_items.map((item, index) => {
                const mc = toNum(item.total_mc);
                const sl = toNum(item.total_sl);
                const cl = toNum(item.total_cutloss);
                const showMC = mc !== 0;
                const showSL = sl !== 0;
                const showCL = cl !== 0;
                const anyEvent = showMC || showSL || showCL;

                const accountLabel = `${item.account_number}`;
                const alias = item.alias ? `${item.alias}` : "";

                return (
                  <TableRow
                    key={index}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": { bgcolor: "action.hover" },
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>

                    <TableCell sx={{ py: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, lineHeight: 1 }}
                      >
                        {accountLabel}
                      </Typography>
                      {alias && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", lineHeight: 1.2 }}
                        >
                          {alias}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          justifyContent: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        {showMC && (
                          <Chip
                            size="small"
                            label={`MC ${fmtUSD(mc)}`}
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        {showSL && (
                          <Chip
                            size="small"
                            label={`SL ${fmtUSD(sl)}`}
                            color="info"
                            variant="outlined"
                          />
                        )}
                        {showCL && (
                          <Chip
                            size="small"
                            label={`CL ${fmtUSD(cl)}`}
                            color="error"
                            variant="outlined"
                          />
                        )}
                        {!anyEvent && (
                          <Chip size="small" label="—" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>

                    {/* <TableCell align="right">
                      {fmtUSD(item.loss_profit)}
                    </TableCell> */}
                    <TableCell align="right">
                      {fmtUSD(item.final_profit_usd)}
                    </TableCell>
                    <TableCell align="right">
                      {fmtIDR(item.final_profit_usd_x_rate_rp)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default InvoicePDF;
